import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {Observer} from "mobx-react";
import styled from "styled-components";
import {useAppStore} from "../../../../../StateManager";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import type {LibraryModel} from "../../../../../data/models/LibraryModel";
import {LibraryModelType} from "../../../../../data/models/LibraryModel";
import {ReactUtils} from "../../../../utils/ReactUtils";
import type {LibraryImage} from "../../../../../data/models/LibraryImage";
import CloudUpArrowIcon from "../../../icons/cloud-arrow-up.svg?react";
import DeleteIcon from "../../../icons/delete.svg?react";
import {NoResultSearchViewV5} from "../../table/NoResultSearchViewV5";
import {FileDropperReactV5, FileDropperStyled} from "../../../interaction/FileDropperReactV5";
import {colorPalette} from "../../../styles/colorPalette";
import {SVGIcon} from "../../../../widgets/button/SVGIcon";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import {ButtonV5} from "../../../button/ButtonV5";
import {IconButtonStyled, IconButtonV5} from "../../../interaction/IconButtonV5";
import {baseDistance, FlexCenter, fontSize, radius, VerticalFlexStyle, zIndex} from "../../../styles/styles";
import {openKeywordsPopupForFile} from "../../../../modules/catalog/create/CatalogUtils";
import {DropdownButtonStyled, DropdownButtonV5} from "../../../interaction/DropdownButtonV5";
import {FileUtils} from "../../../../../utils/file/FileUtils";
import {HorizontalAlignment} from "../../../../../utils/dom/DomUtils";
import {Functions} from "../../../../../utils/function/Functions";
import DotsHorizontalIcon from "../../../icons/dots-horizontal.svg?react";
import DownloadIcon from "../../../icons/download.svg?react";
import {FileDropperWrapperV5} from "../../../interaction/FileDropperWrapperV5";
import {GoogleImagePanelV5} from "./GoogleImagePanelV5";

type LibraryItemType = "image" | "glb";
export type LibraryItem = LibraryImage | LibraryModel;

interface ILibraryItemsProps {
	readonly libraryItems: LibraryItem[];
	readonly selectedLibraryItems: LibraryItem[];
	readonly type: LibraryItemType;
	readonly onFileInputChange: (file: File, keywords: string[]) => Promise<void>;
	readonly onItemsSelected: (items: LibraryItem[]) => void;
	readonly onSetItemActive: (item: LibraryItem) => Promise<void> | void;
	readonly onDeleteSelectedClick: () => Promise<void>;
	readonly onDeleteClick: (item: LibraryItem) => Promise<void>;
}

export const LibraryItemsV5 = (props: ILibraryItemsProps) => {
	const appState = useAppStore((state) => state.appState);
	const [searchString, setSearchString] = useState<string>("");
	const [isGoogleImagePanelOpen, setIsGoogleImagePanelOpen] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);

	const onSearchInput = (value: string) => setSearchString(value);

	const onItemClick = (libraryItem: LibraryItem) => {
		if (KeyboardListener.isCtrlDown) {
			const newSelectedLibraryItems = [...props.selectedLibraryItems];
			const indexOfClicked = newSelectedLibraryItems.indexOf(libraryItem);

			if (indexOfClicked > -1) {
				newSelectedLibraryItems.splice(indexOfClicked, 1);
			} else {
				newSelectedLibraryItems.push(libraryItem);
			}
			props.onItemsSelected(newSelectedLibraryItems);
		} else {
			props.onItemsSelected([libraryItem]);
			props.onSetItemActive(libraryItem);
		}
	};

	const onKeyUp = (event: KeyboardEvent) => {
		if (event.key === KeyboardListener.KEY_DELETE) {
			props.onDeleteSelectedClick();
		}
	};

	const label = props.type === "image" ? "image" : "model";
	const accept = props.type === "image" ? "image/*" : ".glb";

	const onFileInputChange = (files: FileList) => {
		if (files.length > 0) {
			const file = files[0];

			return openKeywordsPopupForFile(file, label, props.onFileInputChange, appState.currentUIVersion);
		}
	};

	const onGoogleImageClick = () => setIsGoogleImagePanelOpen((prevState) => !prevState);

	const onGoogleImagePanelClose = () => setIsGoogleImagePanelOpen(false);

	const onAddItemClick = () => {
		const input = document.createElement("input");

		input.type = "file";
		input.multiple = false;
		input.accept = accept;
		input.onchange = (event: InputEvent) => {
			const files = (event.currentTarget as HTMLInputElement).files;

			onFileInputChange(files);
		};

		input.click();
	};

	const getItems = () => {
		return StringUtils.filterByKeywords(props.libraryItems, searchString) as (LibraryItem | LibraryModel)[];
	};

	useEffect(() => {
		KeyboardListener.getInstance().signals.up.add(onKeyUp);

		return () => {
			KeyboardListener.getInstance().signals.up.remove(onKeyUp);
		};
	});

	const onImageAddFromGoolgeImagePanel = async (file: File, keywords: string[]) => {
		await props.onFileInputChange(file, keywords);
		props.onSetItemActive(props.libraryItems[props.libraryItems.length - 1]);
	};

	return (
		<Observer>
			{() => {
				const items = getItems() as (LibraryItem | LibraryModel)[];
				const isTheStandard = (items[0] as LibraryModel)?.geometryType === LibraryModelType.STANDARD;
				const customItems = isTheStandard ? items.slice(1) : items;

				return (
					<LibraryItemsStyled
						$type={props.type}
						ref={ref}
					>
						<FlexCenter data-cy="library-items-search-field-container">
							<SearchFieldV5
								onInput={onSearchInput}
								value={searchString}
								cyId={`library-items-search-field`}
							/>
							{props.type === "glb" && (
								<>
									<ButtonV5
										onClick={onAddItemClick}
										title={`Add new ${label}`}
										label="Upload"
										type="tertiary"
									>
										<CloudUpArrowIcon />
									</ButtonV5>
									{props.selectedLibraryItems.length > 0 && (
										<ButtonV5
											onClick={props.onDeleteSelectedClick}
											title={`Delete selected ${label}s`}
											label="Delete"
											type="tertiary"
										>
											<DeleteIcon />
										</ButtonV5>
									)}
								</>
							)}
						</FlexCenter>
						{props.type === "image" && (
							<FlexCenter
								$gap="8px"
								data-cy="library-items-google-button-container"
							>
								<GoogleButtonStyled
									data-cy="library-items-google-button"
									className={ReactUtils.cls("googleBtn btn hbox alignCenter", {selected: isGoogleImagePanelOpen === true})}
									onClick={onGoogleImageClick}
								>
									<SVGIcon icon="google" />
									Google Search
								</GoogleButtonStyled>
								<ButtonV5
									onClick={onAddItemClick}
									title={`Add new ${label}`}
									label="Upload"
									type="tertiary"
								>
									<CloudUpArrowIcon />
								</ButtonV5>
								{props.selectedLibraryItems.length > 0 && (
									<ButtonV5
										onClick={props.onDeleteSelectedClick}
										title={`Delete selected ${label}s`}
										label="Delete"
										type="tertiary"
										style={{marginLeft: "auto"}}
									>
										<DeleteIcon />
									</ButtonV5>
								)}
							</FlexCenter>
						)}

						{isGoogleImagePanelOpen ? (
							<GoogleImagePanelV5
								defaultSearchValue={searchString || ""}
								onCloseClick={onGoogleImagePanelClose}
								addImage={onImageAddFromGoolgeImagePanel}
								addedItems={items}
							/>
						) : (
							<>
								{props.libraryItems.length > 0 ? (
									items.length > 0 ? (
										<ItemContainerStyled
											$type={props.type}
											data-cy="library-item-container"
										>
											<FileDropperWrapperV5
												accept={accept}
												onFileInputChange={onFileInputChange}
											>
												<LibraryItemContainerStyled data-cy="library-item-container">
													{customItems.map((libraryItem: LibraryImage | LibraryModel, index: number) => {
														const isSelected = props.selectedLibraryItems.includes(libraryItem);
														const title = libraryItem.keywords.filter((s) => !StringUtils.isBlank(s)).join(", ");
														const style: React.CSSProperties = {
															backgroundImage: `url(${libraryItem.thumbnail})`,
														};

														return (
															<ThumbnailStyled
																key={libraryItem.id}
																style={style}
																className={ReactUtils.cls("thumbnail", {selected: isSelected})}
																onClick={() => onItemClick(libraryItem)}
																title={title}
															>
																<DropdownButtonV5
																	options={[
																		{
																			label: "Download",
																			IconComponent: DownloadIcon,
																			onClick: () => FileUtils.downloadFileFromUrl(libraryItem.url, libraryItem.fileName),
																		},
																		{
																			label: "Delete",
																			IconComponent: DeleteIcon,
																			onClick: () => props.onDeleteClick(libraryItem),
																		},
																	]}
																	horizontalAlignment={HorizontalAlignment.left}
																	optionsZIndex={zIndex.popup + 1}
																	button={
																		<ExportDotsStyled
																			IconComponent={DotsHorizontalIcon}
																			onClick={Functions.emptyFunction}
																		/>
																	}
																	title="Options"
																/>
															</ThumbnailStyled>
														);
													})}
												</LibraryItemContainerStyled>
											</FileDropperWrapperV5>
										</ItemContainerStyled>
									) : (
										<NoResultSearchViewV5 searchString={searchString}></NoResultSearchViewV5>
									)
								) : (
									<FileDropperReactV5
										onFileInputChange={onFileInputChange}
										purpose="Drag and drop or click here to add new images"
										accept={accept}
										multiple={false}
									/>
								)}
							</>
						)}
					</LibraryItemsStyled>
				);
			}}
		</Observer>
	);
};

const LibraryItemsStyled = styled.div<{$type: "image" | "glb"}>`
	${VerticalFlexStyle};
	padding: ${baseDistance.md};
	padding-top: ${(props) => (props.$type === "image" ? "0" : baseDistance.md)};
	gap: ${baseDistance.md};

	${FileDropperStyled} {
		height: 384px;
	}

	.SearchField {
		flex: 1;
	}
`;

const GoogleButtonStyled = styled.div`
	padding: ${baseDistance.sm};
	font-size: ${fontSize.md};
	color: ${colorPalette.primary.c500Primary};
	cursor: pointer;
	border: solid 1px ${colorPalette.primary.c500Primary};
	border-radius: ${radius.sm};
	box-sizing: border-box;

	svg.icon {
		width: 12px;
		height: 12px;
		margin-right: ${baseDistance.sm};
	}

	&.selected {
		background-color: ${colorPalette.primary.c200Light};
		border-radius: ${radius.sm};
	}

	&.selected:hover {
		background-color: ${colorPalette.primary.c200Light};
		border-radius: ${radius.sm};
	}

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
		border-radius: ${radius.sm};
	}
`;

const ItemContainerStyled = styled.div<{$type: LibraryItemType}>`
	overflow-x: hidden;
	overflow-y: scroll;
	max-height: ${(props) => (props.$type === "glb" ? "495px" : "382px")};
`;

const LibraryItemContainerStyled = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 109px);
	grid-gap: ${baseDistance.sm};
`;

const ThumbnailStyled = styled.div`
	width: 109px;
	height: 109px;
	background-color: ${colorPalette.gray.c100};
	position: relative;
	border: 4px solid ${colorPalette.white};

	${IconButtonStyled} {
		visibility: hidden;
		position: absolute;
		right: 0;
		top: 0;
		background-color: ${colorPalette.gray.c100};
	}

	&:hover {
		${IconButtonStyled} {
			visibility: visible;
		}
	}

	&.selected {
		width: 109px;
		height: 109px;
		border: 4px solid ${colorPalette.primary.c500Primary};
	}

	${DropdownButtonStyled} {
		width: 32px;
		right: ${baseDistance.xs};
		top: ${baseDistance.xs};
		position: absolute;

		&.isOpen {
			${IconButtonStyled} {
				visibility: visible;
			}
		}
	}
`;

const ExportDotsStyled = styled(IconButtonV5)`
	svg {
		width: 16px;
		height: 16px;
	}
`;
